import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styles from '../static/styles/BundleVouchers.module.css';
import MessagePopup from '../components/MessagePopup';
import { isAuthenticated } from '../api/auth';
import { requestBundleRedeemOtp, completeBundleRedeem } from '../api/bundle';
import { DEFAULT_CLAIM, getBundle, getStoredBundleCode, getStoredBundleReward, getStoredRedemptionId, hasSeenSplash, markSplashSeen } from '../utils/bundles';
import BundleSplash from '../components/BundleSplash';
import freefireDiamonds from '../static/img/Rewards/freefireDiamonds.png';

const getStoredPhone = () => {
    const rawMsisdn = sessionStorage.getItem('msisdn') || '';
    return rawMsisdn.startsWith('92') ? rawMsisdn : `92${rawMsisdn.replace(/^0/, '')}`;
};

const BundleVouchers = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const bundleCode = location.state?.bundleCode || getStoredBundleCode();
    const bundle = getBundle(bundleCode);
    const baseClaim = bundle?.claim || DEFAULT_CLAIM;
    const apiReward = bundle
        ? (location.state?.bundle?.reward || getStoredBundleReward())
        : null;
    const claim = { ...baseClaim, reward: apiReward || baseClaim.reward };
    const [claimResult, setClaimResult] = useState(null);

    const hasIntro = !!(bundle?.congratsVideo || bundle?.splashVideo);
    const [showIntro, setShowIntro] = useState(() => hasIntro && !hasSeenSplash(bundleCode));

    const dismissIntro = () => {
        markSplashSeen(bundleCode);
        setShowIntro(false);
    };

    const [playerId, setPlayerId] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [popupMessage, setPopupMessage] = useState('');
    const [isPopupOpen, setIsPopupOpen] = useState(false);

    const [isOtpPopupOpen, setIsOtpPopupOpen] = useState(false);
    const [otpDigits, setOtpDigits] = useState(['', '', '', '']);
    const [otpTimer, setOtpTimer] = useState(60);
    const otpInputRefs = useRef([]);

    const [phone, setPhone] = useState(getStoredPhone);

    useEffect(() => {
        document.title = `Claim ${claim.reward} | Jazz ESL`;
    }, [claim.reward]);

    useEffect(() => {
        if (isAuthenticated()) {
            setPhone(getStoredPhone());
            return;
        }

        const timer = setTimeout(() => {
            if (!isAuthenticated()) {
                window.location.href = process.env.REACT_APP_LOGIN_URL;
            }
        }, 3000);

        const handleAuthComplete = () => {
            clearTimeout(timer);
            setPhone(getStoredPhone());
        };

        window.addEventListener('authComplete', handleAuthComplete);

        return () => {
            clearTimeout(timer);
            window.removeEventListener('authComplete', handleAuthComplete);
        };
    }, []);

    useEffect(() => {
        if (!isOtpPopupOpen) {
            return;
        }

        setOtpTimer(60);
        const timerId = setInterval(() => {
            setOtpTimer(prevTimer => Math.max(prevTimer - 1, 0));
        }, 1000);

        setTimeout(() => {
            otpInputRefs.current[0]?.focus();
        }, 0);

        return () => clearInterval(timerId);
    }, [isOtpPopupOpen]);

    const getErrorMessage = (error, fallback = 'An error occurred. Please try again.') => (
        error?.data?.message ||
        error?.message ||
        error?.response?.data?.data?.message ||
        fallback
    );

    const isPlayerIdValid = playerId.length >= 9 && playerId.length <= 11 && /^\d+$/.test(playerId);

    const handleClaim = () => {
        if (isSubmitting) {
            return;
        }

        if (!isPlayerIdValid) {
            setPopupMessage(`Please enter a valid ${claim.playerIdLabel} (9-11 digits).`);
            setIsPopupOpen(true);
            return;
        }

        setIsSubmitting(true);

        requestBundleRedeemOtp()
            .then((response) => {
                if (response.status === 200 && response.data.success) {
                    setOtpDigits(['', '', '', '']);
                    setIsOtpPopupOpen(true);
                } else {
                    const errorMessage =
                        response.data?.data?.message ||
                        response.data?.message ||
                        'OTP request failed. Please try again.';

                    setPopupMessage(errorMessage);
                    setIsPopupOpen(true);
                }

                setIsSubmitting(false);
            })
            .catch((error) => {
                setPopupMessage(getErrorMessage(error, 'OTP request failed. Please try again.'));
                setIsPopupOpen(true);
                setIsSubmitting(false);
            });
    };

    const submitClaim = (otp) => {
        setIsSubmitting(true);

        const redemptionId = bundle ? (location.state?.redemptionId || getStoredRedemptionId()) : null;
        const payload = { phone, otp, [claim.playerIdField]: playerId };
        if (redemptionId) payload.redemption_id = redemptionId;

        completeBundleRedeem(payload)
            .then((response) => {
                if (response.status === 200 && response.data.success) {
                    setIsOtpPopupOpen(false);
                    const thankyouState = {
                        playerId,
                        phone,
                        reward: claim.reward,
                        playerIdLabel: claim.playerIdLabel,
                        processingLine: claim.processingLine,
                    };
                    if (claim.video) {
                        setClaimResult(thankyouState);
                    } else {
                        navigate('/bundle-thankyou', { state: thankyouState });
                    }
                } else {
                    const errorMessage =
                        response.data?.message ||
                        response.data?.data?.message ||
                        'Claim failed. Please try again.';
                    setPopupMessage(errorMessage);
                    setIsPopupOpen(true);
                }
                setIsSubmitting(false);
            })
            .catch((error) => {
                setPopupMessage(getErrorMessage(error, 'Claim failed. Please try again.'));
                setIsPopupOpen(true);
                setIsSubmitting(false);
            });
    };

    const handleOtpSubmit = () => {
        if (isSubmitting) {
            return;
        }

        const cleanOtp = otpDigits.join('');
        if (cleanOtp.length !== 4) {
            setPopupMessage('Please enter the 4-digit OTP.');
            setIsPopupOpen(true);
            return;
        }

        submitClaim(cleanOtp);
    };

    const handleOtpDigitChange = (index, value) => {
        const digit = value.replace(/[^0-9]/g, '').slice(-1);
        const nextOtpDigits = [...otpDigits];
        nextOtpDigits[index] = digit;
        setOtpDigits(nextOtpDigits);

        if (digit && index < otpInputRefs.current.length - 1) {
            otpInputRefs.current[index + 1]?.focus();
        }
    };

    const handleOtpKeyDown = (index, event) => {
        if (event.key === 'Enter') {
            handleOtpSubmit();
            return;
        }

        if (event.key === 'Backspace' && !otpDigits[index] && index > 0) {
            otpInputRefs.current[index - 1]?.focus();
        }
    };

    const handleOtpPaste = (event) => {
        event.preventDefault();
        const pastedOtp = event.clipboardData.getData('text').replace(/[^0-9]/g, '').slice(0, 4);
        if (!pastedOtp) {
            return;
        }

        const nextOtpDigits = ['', '', '', ''];
        pastedOtp.split('').forEach((digit, index) => {
            nextOtpDigits[index] = digit;
        });
        setOtpDigits(nextOtpDigits);

        const nextFocusIndex = Math.min(pastedOtp.length, 3);
        otpInputRefs.current[nextFocusIndex]?.focus();
    };

    const handleOtpResend = () => {
        if (otpTimer > 0 || isSubmitting) {
            return;
        }

        setIsSubmitting(true);
        requestBundleRedeemOtp()
            .then((response) => {
                if (response.status === 200 && response.data.success) {
                    setOtpDigits(['', '', '', '']);
                    setOtpTimer(60);
                    otpInputRefs.current[0]?.focus();
                } else {
                    const errorMessage = response.data?.data?.message || response.data?.message || 'OTP request failed. Please try again.';
                    setPopupMessage(errorMessage);
                    setIsPopupOpen(true);
                }
                setIsSubmitting(false);
            })
            .catch((error) => {
                setPopupMessage(getErrorMessage(error, 'OTP request failed. Please try again.'));
                setIsPopupOpen(true);
                setIsSubmitting(false);
            });
    };

    return (
        <div className={styles.container}>

            {/* HERO */}
            <div className={styles.hero}>
                <img src={claim.heroImage} alt={claim.heroAlt} className={styles.heroImage} />
                <div className={styles.heroOverlay}>
                    <h2 className={styles.heroTitle}>{claim.reward}</h2>
                    <p className={styles.heroSubtitle}>{claim.heroSubtitle}</p>
                </div>
            </div>

            {/* FORM */}
            <div className={styles.card}>
                <span className={styles.sectionLabel}>ENTER YOUR DETAILS</span>

                <div className={styles.inputGroup}>
                    <label className={styles.freefireIdLabel}>{claim.playerIdLabel}</label>
                    <input
                        type="text"
                        className={styles.freefireIdInput}
                        value={playerId}
                        onChange={(e) => setPlayerId(e.target.value.replace(/[^0-9]/g, ''))}
                        placeholder="e.g. 123456789"
                        minLength={9}
                        maxLength={11}
                        required
                    />
                    <small>{claim.playerIdHint}</small>
                </div>

                <button
                    className={styles.ctaBtn}
                    onClick={handleClaim}
                    disabled={isSubmitting}
                >
                    {isSubmitting ? 'PROCESSING...' : 'CLAIM NOW →'}
                </button>

                <p className={styles.note}>
                    Delivery within 24–48 hours of verification
                </p>
            </div>

            <MessagePopup
                isOpen={isPopupOpen}
                message={popupMessage}
                onClose={() => setIsPopupOpen(false)}
            />

            {isOtpPopupOpen && (
                <div className={styles.otpOverlay} onClick={() => !isSubmitting && setIsOtpPopupOpen(false)}>
                    <div className={styles.otpPopup} onClick={(e) => e.stopPropagation()}>
                        <img src={freefireDiamonds} alt="" style={{ width: 64, height: 64, objectFit: 'contain', marginBottom: 8 }} />
                        <h3>ENTER OTP</h3>
                        <p>Enter the OTP sent to your registered contact number.</p>
                        <div className={styles.otpInputs}>
                            {otpDigits.map((digit, index) => (
                                <input
                                    key={index}
                                    ref={element => {
                                        otpInputRefs.current[index] = element;
                                    }}
                                    type="text"
                                    inputMode="numeric"
                                    value={digit}
                                    onChange={e => handleOtpDigitChange(index, e.target.value)}
                                    onKeyDown={e => handleOtpKeyDown(index, e)}
                                    onPaste={handleOtpPaste}
                                    maxLength={1}
                                    aria-label={`OTP digit ${index + 1}`}
                                />
                            ))}
                        </div>
                        <button
                            className={styles.otpTimer}
                            type="button"
                            onClick={handleOtpResend}
                            disabled={otpTimer > 0 || isSubmitting}
                        >
                            {otpTimer > 0 ? `Resend OTP in ${otpTimer}s` : 'Resend OTP'}
                        </button>
                        <div className={styles.otpActions}>
                            <button
                                className={styles.otpCancelBtn}
                                type="button"
                                onClick={() => setIsOtpPopupOpen(false)}
                                disabled={isSubmitting}
                            >
                                CANCEL
                            </button>
                            <button
                                className={styles.otpSubmitBtn}
                                type="button"
                                onClick={handleOtpSubmit}
                                disabled={isSubmitting || otpDigits.join('').length !== 4}
                            >
                                {isSubmitting ? 'VERIFYING...' : 'SUBMIT'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showIntro && hasIntro && !claimResult && (
                <BundleSplash
                    bundle={{ ...bundle, splashCta: claim.claimCta || 'CLAIM MY REWARD →' }}
                    onContinue={dismissIntro}
                />
            )}

            {claimResult && claim.video && (
                <BundleSplash
                    bundle={{ ...bundle, congratsVideo: claim.video, splashVideo: null }}
                    onContinue={() => navigate('/bundle-thankyou', { state: claimResult })}
                />
            )}

        </div>
    );
};

export default BundleVouchers;